import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Languages, Sparkles, Loader2, Check } from "lucide-react";

/**
 * TranslationTabs — reusable EN/ES tab strip for the case/insight/job editors.
 *
 * Data contract (shape stored on the document):
 *   item.translations = {
 *     en: { title, situation, solution, strategy, deliveries, results, ... },
 *     es: { title, ... },
 *   }
 *
 * Props:
 *   value        — the current translations object on the item ({en:{}, es:{}})
 *   onChange(v)  — called with the new translations object when the user edits a
 *                  translated field OR the AI populates the tabs.
 *   sourceFields — { field: value } — the PT source fields to translate.
 *                  Pass the up-to-date PT values so the AI translates the latest text.
 *   fields       — [{ key, label, rich?: bool, area?: bool, arr?: bool }] — schema of the
 *                  fields to expose in the EN/ES tabs.
 *   context      — "case" | "insight" | "job" | "generic" — sent to the API for tone hints.
 *
 * The PT ("Português") tab renders nothing — the parent editor keeps rendering
 * the original fields above the tabs. Only EN/ES tabs render translated inputs.
 */
export default function TranslationTabs({ value = {}, onChange, sourceFields, fields, context = "generic", testidPrefix = "tt" }) {
  const [active, setActive] = useState("pt");
  const [translating, setTranslating] = useState(null); // null | "en" | "es" | "all"

  const t = value || {};
  const setLangField = (l, k, v) => onChange({ ...t, [l]: { ...(t[l] || {}), [k]: v } });
  const setLangObj = (l, obj) => onChange({ ...t, [l]: { ...(t[l] || {}), ...obj } });

  const translate = async (targets) => {
    setTranslating(targets.length === 1 ? targets[0] : "all");
    try {
      const { data } = await api.post("/admin/translate", {
        fields: sourceFields,
        target_langs: targets,
        context,
        preserve_html: true,
      });
      const next = { ...t };
      targets.forEach((tl) => {
        if (data[tl]) next[tl] = { ...(next[tl] || {}), ...data[tl] };
      });
      onChange(next);
      toast.success(`Tradução ${targets.join(" + ").toUpperCase()} preenchida com IA. Revise e edite se precisar.`);
    } catch (e) {
      toast.error("Falha ao traduzir. Verifique a chave LLM.");
    } finally {
      setTranslating(null);
    }
  };

  const stats = (l) => {
    const obj = t[l] || {};
    const filled = fields.filter((f) => obj[f.key] && String(obj[f.key]).trim()).length;
    return `${filled}/${fields.length}`;
  };

  const tabBtn = (id, label, flag) => (
    <button
      onClick={() => setActive(id)}
      data-testid={`${testidPrefix}-tab-${id}`}
      className={`flex-1 md:flex-none px-4 py-2 text-sm font-semibold uppercase tracking-widest border-b-2 transition-colors ${active === id ? "border-accent-orange text-black" : "border-transparent text-neutral-400 hover:text-black"}`}
    >
      <span className="mr-1">{flag}</span> {label}
      {id !== "pt" && <span className="ml-2 text-[10px] font-mono text-neutral-500">{stats(id)}</span>}
    </button>
  );

  return (
    <div className="border border-black/10 rounded-md mt-6" data-testid={testidPrefix}>
      <header className="flex items-center justify-between border-b border-black/10 flex-wrap gap-3 px-3 pt-2">
        <div className="flex items-center gap-1">
          {tabBtn("pt", "Português", "🇧🇷")}
          {tabBtn("en", "English", "🇺🇸")}
          {tabBtn("es", "Español", "🇪🇸")}
        </div>
        {active !== "pt" && (
          <button
            onClick={() => translate([active])}
            disabled={translating}
            data-testid={`${testidPrefix}-translate-current`}
            className="text-xs bg-black text-white px-3 py-2 rounded-full font-semibold hover:bg-accent-orange transition-colors disabled:opacity-60 my-1.5"
          >
            {translating === active ? <><Loader2 className="w-3 h-3 inline animate-spin mr-1" /> Traduzindo…</> : <><Sparkles className="w-3 h-3 inline mr-1" /> Traduzir com IA para {active.toUpperCase()}</>}
          </button>
        )}
        {active === "pt" && (
          <button
            onClick={() => translate(["en", "es"])}
            disabled={translating}
            data-testid={`${testidPrefix}-translate-all`}
            className="text-xs bg-accent-yellow text-black px-3 py-2 rounded-full font-semibold hover:bg-black hover:text-white transition-colors disabled:opacity-60 my-1.5"
          >
            {translating === "all" ? <><Loader2 className="w-3 h-3 inline animate-spin mr-1" /> Traduzindo EN + ES…</> : <><Languages className="w-3 h-3 inline mr-1" /> Traduzir automaticamente EN + ES</>}
          </button>
        )}
      </header>

      {active === "pt" ? (
        <div className="p-4 bg-neutral-50 text-sm text-neutral-600">
          <p><b>Português é o idioma-fonte</b> — edite os campos originais acima. As traduções abaixo (EN/ES) usam o texto em PT como base.</p>
          <p className="text-xs text-neutral-500 mt-2">
            Progresso · <b>EN {stats("en")}</b> · <b>ES {stats("es")}</b> preenchidos.
          </p>
        </div>
      ) : (
        <div className="p-4 space-y-3 bg-neutral-50" data-testid={`${testidPrefix}-panel-${active}`}>
          {fields.map((f) => {
            const val = (t[active] && t[active][f.key]) ?? "";
            const src = sourceFields[f.key] ?? "";
            const isArr = f.arr;
            const inputVal = isArr ? (Array.isArray(val) ? val.join(", ") : val || "") : val;
            const onInput = (raw) => {
              const parsed = isArr ? String(raw).split(",").map((s) => s.trim()).filter(Boolean) : raw;
              setLangField(active, f.key, parsed);
            };
            return (
              <div key={f.key}>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs uppercase tracking-widest text-neutral-500">
                    {f.label}
                    {val && String(val).trim() && <Check className="w-3 h-3 inline text-green-600 ml-1" />}
                  </label>
                  <span className="text-[10px] text-neutral-400 italic max-w-[60%] truncate" title={typeof src === "string" ? src : JSON.stringify(src)}>
                    PT: {typeof src === "string" ? (src.length > 60 ? src.slice(0, 60) + "…" : src) : Array.isArray(src) ? src.join(", ").slice(0, 60) : ""}
                  </span>
                </div>
                {f.area ? (
                  <textarea rows={3} value={inputVal} onChange={(e) => onInput(e.target.value)}
                    data-testid={`${testidPrefix}-${active}-${f.key}`}
                    className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
                ) : (
                  <input type="text" value={inputVal} onChange={(e) => onInput(e.target.value)}
                    data-testid={`${testidPrefix}-${active}-${f.key}`}
                    className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
                )}
              </div>
            );
          })}
          <div className="text-[11px] text-neutral-400 pt-2">Deixe qualquer campo em branco para usar automaticamente o valor em português.</div>
        </div>
      )}
    </div>
  );
}
